/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import moment from 'moment';
import Joi from 'joi-browser';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import PropTypes from 'prop-types';
import {
  Alert,
  Skeleton,
  Box,
  Grid,
  Avatar,
  Typography,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Button,
  Divider,
  TextField,
  IconButton,
  useMediaQuery,
  TableBody,
  Table,
  TableRow,
  TableHead,
  TableCell,
  Chip,
  CircularProgress,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import Close from '@mui/icons-material/Delete';
import Phone from '@mui/icons-material/Phone';
import Email from '@mui/icons-material/Email';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import makeStyles from '@mui/styles/makeStyles';
import useTheme from '@mui/styles/useTheme';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import {
  Delete,
  NoAccounts,
  UploadFile,
} from '@mui/icons-material';
import {
  updateUser,
  deleteUser,
  getBarnUserFile,
  addBarnUserFile,
} from '../../../../../redux/user/actions';
import ReactFileUpload from '../../../../../components/ReactFileUpload';

import UserRoles from '../UserRoles';
import styles from './styles';
import { blueGrey } from '../../../../../utils/brandColors';
import AboutUserRolesModal from '../AboutUserRolesModal';
import QuestriUserService from '../../../../../services/QuestriUserService';
import Horse from '../../../../../components/Horse';
import AddEditModal from '../../../../horse/Horse/components/HorseAccessCard/components/AddEditModal';
import BarnHorsesAutocomplete from '../../../../../components/Autocompletes/BarnHorses';

const useStyles = makeStyles(styles);
const isImage = /.png|jpeg|jpg|gif$/;

const DEFAULT_VALIDATION = {
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
}

export const UserProfileComponent = (props) => {
  const {
    tenant,
    tenantUser,
    open,
    onClose,
  } = props;
  const fullScreen = useMediaQuery(useTheme().breakpoints.down('sm'));
  const dispatch = useDispatch();
  const classes = useStyles();

  const user = useSelector((state) => state.user.user);
  const { barnUsers } = useSelector((state) => state.barn);

  const [openAboutRolesModal, setOpenAboutRolesModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRemoveAccount, setConfirmRemoveAccount] = useState(false);
  const [loadingHorseAccess, setLoadingHorseAccess] = useState(false);
  const [openAddEditModal, setOpenAddEditModal] = React.useState(false);
  const [selectedHorseAccess, setSelectedHorseAccess] = React.useState(null);
  const [horseAccess, setHorseAccess] = useState([]);

  // Form Fields
  const [validation, setValidation] = useState(DEFAULT_VALIDATION);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editAddressLine1, setEditAddressLine1] = useState('');
  const [editAddressLine2, setEditAddressLine2] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editStateProvince, setEditStateProvince] = useState('');
  const [editZipPostal, setEditZipPostal] = useState('');
  const [editEmergencyContact, setEditEmergencyContact] = useState({});
  const [editReferenceNumbers, setEditReferenceNumbers] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [attachmentsToRemove, setAttachmentsToRemove] = useState([]);
  const [downloadingFile, setDownloadingFile] = useState(null);

  const handleDownload = (file) => {
    setDownloadingFile(file);
    dispatch(getBarnUserFile(tenantUser.id, file, true)).then(() => {
      setDownloadingFile(null);
    }).catch((error) => {
      console.error(error);
      setDownloadingFile(null);
    });
  };

  React.useEffect(() => {
    if (tenantUser) {
      setHorseAccess([]);
      setDeleting(false);
      setSaving(false);
      setConfirmDelete(false);
      setConfirmRemoveAccount(false);
      setValidation(DEFAULT_VALIDATION);
      setEditFirstName(tenantUser.firstName);
      setEditLastName(tenantUser.lastName);
      setEditEmail(tenantUser.email);
      setEditPhone(tenantUser.phone);
      setEditAddressLine1(tenantUser.addressLine1);
      setEditAddressLine2(tenantUser.addressLine2);
      setEditCity(tenantUser.city);
      setEditStateProvince(tenantUser.stateProvinceCounty);
      setEditZipPostal(tenantUser.zipOrPostalcode);
      setEditEmergencyContact(tenantUser.emergencyContact || {});
      setEditReferenceNumbers(tenantUser.referenceNumbers || {});
      setAttachments([]);
      setAttachmentsToRemove([])
      setLoadingHorseAccess(true);
      QuestriUserService.getUserHorseAccess(tenantUser.id).then((data) => {
        setHorseAccess(data);
        setLoadingHorseAccess(false);
      }).catch((error) => {
        console.error(error);
        setLoadingHorseAccess(false);
      })
    }
  }, [tenantUser]);

  const handleRemoveAccount = () => {
    setDeleting(true);
    dispatch(updateUser(tenantUser.id, { removeAuth0Account: true }, false)).then(() => {
      setDeleting(false);
      onClose(true);
    }).catch(() => {
      setDeleting(false);
    })
  }

  const addAttachmetnsAndPatchUser = (updatedUser) => {
    const allRequests = [];
    for (let i = 0; i < attachments.length; i += 1) {
      allRequests.push(dispatch(addBarnUserFile(updatedUser.id, attachments[i])));
    }

    // once all done, process
    Promise.all(allRequests).then((values) => {
      // eslint-disable-next-line no-console
      dispatch(updateUser(tenantUser.id, { attachments: [...((updatedUser.attachments) || []), ...values] }, false)).then(() => {
        setSaving(false);
        onClose(tenantUser);
      }).catch((error) => {
        setSaving(false);
        console.error(error);
      });
    });
  }

  const handleSaveName = () => {
    if (editEmail && Joi.validate(editEmail, Joi.string().email()).error) {
      setValidation({
        ...validation,
        email: 'Not a valid email',
      });
      return;
    }
    setValidation({
      ...validation,
      email: '',
    });

    if (editPhone && Joi.string().regex(/^[0-9]{10}$/)) {
      if (editFirstName.length < 2 || editFirstName.length > 50) {
        setValidation({
          ...validation,
          firstName: 'First name should be between 2 and 50 characters',
        });
        return;
      }
    }
    setValidation({
      ...validation,
      firstName: '',
    });
    if (editLastName.length < 2 || editLastName.length > 50) {
      setValidation({
        ...validation,
        lastName: 'Last name should be between 2 and 50 characters',
      });
      return;
    }
    setValidation({
      ...validation,
      lastName: '',
    });

    if (editEmail && !tenantUser.auth0UserId
      && !!barnUsers.find((e) => e.id !== tenantUser.id && e.email
        && e.email.toLowerCase() === editEmail.toLowerCase())) {
      setValidation({
        ...validation,
        email: 'A user with this email already exists in your organization',
      });
      return;
    }
    setSaving(true);
    let updatedAttachmentsList = null;
    if (attachmentsToRemove && attachmentsToRemove.length > 0 && tenantUser.attachments && tenantUser.attachments.length > 0) {
      // remove any removed files
      updatedAttachmentsList = tenantUser.attachments.filter((f) => attachmentsToRemove.indexOf(f) < 0);
    }
    dispatch(updateUser(tenantUser.id, {
      email: editEmail || null,
      firstName: editFirstName,
      lastName: editLastName,
      phone: editPhone || null,
      addressLine1: editAddressLine1 || null,
      addressLine2: editAddressLine2 || null,
      city: editCity || null,
      zipOrPostalcode: editZipPostal || null,
      stateProvinceCounty: editStateProvince || null,
      emergencyContact: editEmergencyContact,
      referenceNumbers: editReferenceNumbers,
      attachments: updatedAttachmentsList || null,
    }, false)).then((updatedUser) => {
      if (attachments.length > 0) {
        // upload files then patch note with files
        addAttachmetnsAndPatchUser(updatedUser);
      } else {
        setSaving(false);
        onClose(tenantUser);
      }
    }).catch(() => {
      setSaving(false);
    })
  };

  const handleDeleteUser = () => {
    setDeleting(true);
    dispatch(deleteUser(
      tenantUser.id,
    )).then(() => {
      setDeleting(false);
      onClose(true);
    }).catch(() => {
      setDeleting(false);
    });
  }

  const onlyOneOwner = barnUsers.filter((bu) => bu.roles.indexOf('owner') >= 0).length <= 1;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      aria-labelledby="user-role-info"
      aria-describedby="user-role-info"
      id="user-role-info-dialog"
      className={classes.root}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="remove-user-title">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Avatar
            src={tenantUser && tenantUser.avatar}
            alt={tenantUser && tenantUser.userFullname}
            sx={{
              width: 56,
              height: 56,
              mr: 2,
            }}
          />
          <Box>
            <Typography variant="h4">{tenantUser && tenantUser.userFullName}</Typography>
            {
              tenantUser && (
                <UserRoles
                  tenant={tenant}
                  tenantUser={tenantUser}
                  onlyOneOwner={onlyOneOwner}
                  isLoading={false}
                  infoButton={(
                    <Tooltip title="About User Roles">
                      <IconButton onClick={() => setOpenAboutRolesModal(true)}>
                        <InfoOutlined />
                      </IconButton>
                    </Tooltip>
                  )}
                />
              )
            }
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            borderRadius: 2,
            backgroundColor: blueGrey[50],
            p: 0.5,
            mb: 1,
          }}
        >
          <Typography sx={{ ml: 2 }}>Contact Information</Typography>
        </Box>
        <Box
          component="form"
          sx={{
            '& .MuiTextField-root': {
              m: 1,
              width: '23ch',
            },
          }}
        >
          <TextField
            error={!!validation.firstName}
            margin="normal"
            variant="outlined"
            id="first-name"
            value={editFirstName}
            onChange={(e) => setEditFirstName(e.target.value)}
            label="First Name"
            helperText={validation.lastName}
          />
          <TextField
            error={!!validation.lastName}
            margin="normal"
            variant="outlined"
            id="last-name"
            value={editLastName}
            onChange={(e) => setEditLastName(e.target.value)}
            label="Last Name"
            helperText={validation.lastName}
          />
          <Tooltip title={tenantUser && tenantUser.auth0UserId && 'This user already has an account created under this email. Email cannot be modified.'}>
            <TextField
              error={!!validation.email}
              margin="normal"
              variant="outlined"
              id="email"
              disabled={!!(tenantUser && tenantUser.auth0UserId)}
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              label="Email"
              helperText={validation.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />
          </Tooltip>
          <TextField
            error={!!validation.phone}
            margin="normal"
            variant="outlined"
            id="phone"
            value={editPhone}
            onChange={(e) => setEditPhone(e.target.value)}
            label="Phone"
            helperText={validation.phone}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box
          sx={{
            borderRadius: 2,
            backgroundColor: blueGrey[50],
            p: 0.5,
            mt: 2,
            mb: 1,
          }}
        >
          <Typography sx={{ ml: 2 }}>Address</Typography>
        </Box>
        <Box
          component="form"
          sx={{ '& .MuiTextField-root': { m: 1 } }}
        >
          <TextField
            margin="normal"
            variant="outlined"
            id="addresss-1"
            value={editAddressLine1}
            onChange={(e) => setEditAddressLine1(e.target.value)}
            label="Address"
            sx={{ width: '30ch ' }}
          />
          <TextField
            margin="normal"
            variant="outlined"
            id="addresss-2"
            value={editAddressLine2}
            onChange={(e) => setEditAddressLine2(e.target.value)}
            label="Apartment, Suite, etc."
            sx={{ width: '15ch ' }}
          />
          <TextField
            margin="normal"
            variant="outlined"
            id="zip-postal"
            value={editZipPostal}
            onChange={(e) => setEditZipPostal(e.target.value)}
            label="Zip/Postal Code"
            sx={{ width: '15ch ' }}
            type="number"
          />
          <TextField
            margin="normal"
            variant="outlined"
            id="city"
            value={editCity}
            onChange={(e) => setEditCity(e.target.value)}
            label="City"
            sx={{ width: '23ch ' }}
          />
          <TextField
            margin="normal"
            variant="outlined"
            id="state-province"
            value={editStateProvince}
            onChange={(e) => setEditStateProvince(e.target.value)}
            label="State/Province"
            sx={{ width: '23ch ' }}
          />

        </Box>
        <Box
          sx={{
            borderRadius: 2,
            backgroundColor: blueGrey[50],
            p: 0.5,
            mt: 2,
            mb: 1,
          }}
        >
          <Typography sx={{ ml: 2 }}>Emergency Contact</Typography>
        </Box>
        <Grid container>
          <Grid item>
            <Box
              component="form"
              sx={{ '& .MuiTextField-root': { m: 1 } }}
            >
              <TextField
                margin="normal"
                variant="outlined"
                id="emergency-contact-1"
                value={editEmergencyContact.name}
                onChange={(e) => setEditEmergencyContact({
                  ...editEmergencyContact,
                  name: e.target.value,
                })}
                label="Primary Contact Name"
                sx={{ width: '23ch ' }}
              />

              <TextField
                margin="normal"
                variant="outlined"
                id="emergency-contact-phone"
                value={editEmergencyContact.phone}
                onChange={(e) => setEditEmergencyContact({
                  ...editEmergencyContact,
                  phone: e.target.value,
                })}
                label="Primary Contact Phone"
                sx={{ width: '23ch ' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone />
                    </InputAdornment>
                  ),
                }}
              />

            </Box>

            <Box
              component="form"
              sx={{ '& .MuiTextField-root': { m: 1 } }}
            >
              <TextField
                margin="normal"
                variant="outlined"
                id="emergency-contact-2"
                value={editEmergencyContact.nameBackup}
                onChange={(e) => setEditEmergencyContact({
                  ...editEmergencyContact,
                  nameBackup: e.target.value,
                })}
                label="Secondary Contact Name"
                sx={{ width: '23ch ' }}
              />

              <TextField
                margin="normal"
                variant="outlined"
                id="emergency-contact-phone-2"
                value={editEmergencyContact.phoneBackup}
                onChange={(e) => setEditEmergencyContact({
                  ...editEmergencyContact,
                  phoneBackup: e.target.value,
                })}
                label="Secondary Contact Phone"
                sx={{ width: '23ch ' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>

          <Grid item>
            <Box
              component="form"
              sx={{ '& .MuiTextField-root': { m: 1 } }}
            >
              <TextField
                margin="normal"
                variant="outlined"
                id="emergency-comment"
                multiline
                rows={4}
                value={editEmergencyContact.comments}
                onChange={(e) => setEditEmergencyContact({
                  ...editEmergencyContact,
                  comments: e.target.value,
                })}
                label="Additional Comments or Instructions"
                sx={{ width: '40ch ' }}
              />
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            borderRadius: 2,
            backgroundColor: blueGrey[50],
            p: 0.5,
            mt: 2,
            mb: 1,
          }}
        >
          <Typography sx={{ ml: 2 }}>Reference Numbers & Identifications</Typography>
        </Box>
        <Box>
          {
            Object.keys(editReferenceNumbers).map((num) => (
              <Box
                key={`ref-number-${num}`}
                component="form"
                sx={{
                  '& .MuiTextField-root': { m: 1 },
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <TextField
                  margin="normal"
                  size="small"
                  variant="outlined"
                  id={`ref-num-key-${num}`}
                  defaultValue={num}
                  onBlur={(e) => {
                    const deepCopy = JSON.parse(JSON.stringify(editReferenceNumbers)); // deep copy
                    const currentVal = JSON.parse(JSON.stringify(editReferenceNumbers[num])); // deep copy
                    // first remove old key
                    delete deepCopy[num];

                    setEditReferenceNumbers({
                      ...deepCopy,
                      [e.target.value]: currentVal,
                    })
                  }}
                  label="ID or Name"
                  sx={{ width: '23ch ' }}
                />
                <TextField
                  margin="normal"
                  size="small"
                  variant="outlined"
                  id={`ref-num-val-${num}`}
                  value={editReferenceNumbers[num]}
                  onChange={(e) => setEditReferenceNumbers({
                    ...editReferenceNumbers,
                    [num]: e.target.value,
                  })}
                  label="Value"
                  sx={{ width: '23ch ' }}
                />
                <Button
                  size="small"
                  onClick={() => {
                    const deepCopy = JSON.parse(JSON.stringify(editReferenceNumbers)); // deep copy
                    delete deepCopy[num];
                    setEditReferenceNumbers(deepCopy);
                  }}
                  startIcon={<Close color="danger" />}
                >Remove
                </Button>
              </Box>
            ))
          }
          <Button
            size="small"
            sx={{ m: 1 }}
            variant="outlined"
            onClick={() => {
              setEditReferenceNumbers({
                ...editReferenceNumbers,
                'NEW ID': '',
              });
            }}
            startIcon={<Add />}
          >Add New ID or Reference Number
          </Button>
        </Box>

        <Box
          sx={{
            borderRadius: 2,
            backgroundColor: blueGrey[50],
            p: 0.5,
            mt: 2,
            mb: 1,
          }}
        >
          <Typography sx={{ ml: 2 }}>File Attachments</Typography>
        </Box>

        <Grid container spacing={1} sx={{ p: 1 }}>

          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
              }}
            >
              <ReactFileUpload
                value={attachments}
                onChange={setAttachments}
                title="Attach files to this user"
                disabled={deleting || saving}
                dense
                buttonProps={{ size: 'small' }}
              />
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flex: 1,
                  flexWrap: 'wrap',
                }}
              >
                {
                  tenantUser && tenantUser.attachments
                  && tenantUser.attachments.filter((file) => attachmentsToRemove.indexOf(file) < 0).map((file) => (
                    <Box key={`file-${file}`}>
                      <Tooltip title="Click to download">
                        <Chip
                          onClick={() => {
                            handleDownload(file, file.match(isImage))
                          }}
                          icon={downloadingFile === file
                            ? <CircularProgress size={18} />
                            : <UploadFile />}
                          variant="outlined"
                          sx={{ maxWidth: 200 }}
                          label={file}
                          deleteIcon={<Tooltip title="Remove attachment from user"><Delete /></Tooltip>}
                          onDelete={() => { setAttachmentsToRemove([...attachmentsToRemove, file]) }}
                        />
                      </Tooltip>
                    </Box>
                  ))
                }
                {
                  tenantUser && (!tenantUser.attachments
                    || tenantUser.attachments.filter((file) => attachmentsToRemove.indexOf(file) < 0).length === 0)
                  && (
                    <Typography
                      sx={{
                        mb: 1,
                        fontWeight: 300,
                      }}
                    >No File Attachments
                    </Typography>
                  )
                }
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            borderRadius: 2,
            backgroundColor: blueGrey[50],
            p: 0.5,
            mt: 2,
            mb: 1,
          }}
        >
          <Typography sx={{ ml: 2 }}>Horse Access</Typography>
        </Box>
        {
          loadingHorseAccess && (
            <Box sx={{ m: 1 }}>
              <Skeleton variant="rounded" width={450} height={40} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" width={450} height={40} />
            </Box>
          )
        }
        {
          !loadingHorseAccess && (

            <Box sx={{ m: 1 }}>
              {
                (tenantUser && (tenantUser.roles.indexOf('owner') >= 0 || tenantUser.roles.indexOf('admin') >= 0 || tenantUser.roles.indexOf('staff') >= 0))
                  ? (
                    <Typography sx={{ fontWeight: 300 }}>
                      This user has owner, admin, or staff level permissions. They will have acess to all horses in your organization.
                    </Typography>
                  )
                  : (
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell align="left">Horse</TableCell>
                          <TableCell align="left">Access Type</TableCell>
                          <TableCell align="left">Note</TableCell>
                          <TableCell align="left">Ownership</TableCell>
                          <TableCell align="left">Access Start</TableCell>
                          <TableCell align="left">Access End</TableCell>
                          <TableCell
                            align="center"
                            style={{
                              maxWidth: 90,
                              minWidth: 90,
                            }}
                          >Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {
                          horseAccess.length === 0
                            ? (
                              <Typography sx={{ fontWeight: 300 }}>No access have been given to any horses in your organization.</Typography>
                            )
                            : (
                              horseAccess.map((ha) => (
                                <TableRow key={ha.id}>
                                  <TableCell align="left"><Horse horse={ha.horse} /></TableCell>
                                  <TableCell align="left">{ha.type}</TableCell>
                                  <TableCell align="left">
                                    {ha.accessNote && <Typography variant="caption" component="div">{`"${ha.accessNote}"`}</Typography>}
                                  </TableCell>
                                  <TableCell align="left">
                                    {ha.owner && `${ha.ownershipPercentage}%`}
                                    {ha.owner && ha.ownershipNotes && <Typography variant="caption" component="div">{`"${ha.ownershipNotes}"`}</Typography>}
                                  </TableCell>
                                  <TableCell align="left">{moment(ha.startDate).format('ll')}</TableCell>
                                  <TableCell align="left">{ha.endDate && moment(ha.endDate).format('ll')}</TableCell>
                                  <TableCell
                                    align="center"
                                    style={{
                                      maxWidth: 90,
                                      minWidth: 90,
                                    }}
                                  >
                                    <IconButton
                                      style={{ padding: 4 }}
                                      onClick={
                                        () => { setSelectedHorseAccess(ha); setOpenAddEditModal(true); }
                                      }
                                    >
                                      <Edit />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))
                            )
                        }
                        <TableRow>
                          <TableCell>
                            Add New Horse Access
                          </TableCell>
                          <TableCell sx={{ minWidth: 200 }}>
                            <BarnHorsesAutocomplete
                              multiple={false}
                              limit={1}
                              onSelect={(e, newHorse) => {
                                setSelectedHorseAccess({
                                  horse: newHorse.horse,
                                  user: tenantUser,
                                }); setOpenAddEditModal(true);
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  )
              }
            </Box>
          )
        }

        <Divider />
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            {
              (user && tenantUser && user.auth0UserId !== tenantUser.auth0UserId && !confirmRemoveAccount) && (
                <Tooltip title="Remove the user from your Questri organization and removed any system/account access">
                  <LoadingButton
                    disabled={deleting || saving}
                    loading={deleting}
                    onClick={() => {
                      if (!confirmDelete) {
                        setConfirmDelete(true);
                      } else {
                        handleDeleteUser();
                      }
                    }}
                    color="error"
                    startIcon={<Close />}
                    variant={confirmDelete
                      ? 'contained'
                      : 'default'}
                    id="delete-button"
                  >
                    {!confirmDelete
                      ? 'Delete'
                      : 'Confirm Delete User'}
                  </LoadingButton>
                </Tooltip>
              )
            }
            {
              (user && tenantUser && tenantUser.auth0UserId && user.auth0UserId && user.auth0UserId !== tenantUser.auth0UserId && !confirmDelete) && (
                <Tooltip title="Keep the user and data associated with it, but remove the login/account access.">
                  <LoadingButton
                    disabled={deleting || saving}
                    loading={deleting}
                    onClick={() => {
                      if (!confirmRemoveAccount) {
                        setConfirmRemoveAccount(true);
                      } else {
                        handleRemoveAccount();
                      }
                    }}
                    color="warning"
                    startIcon={<NoAccounts />}
                    variant={confirmRemoveAccount
                      ? 'contained'
                      : 'default'}
                    id="remove-button"
                  >
                    {!confirmRemoveAccount
                      ? 'Remove Account/Login Access'
                      : 'Confirm Remove Account'}
                  </LoadingButton>
                </Tooltip>
              )
            }

            {confirmRemoveAccount && (
              <Box
                sx={{
                  width: '100%',
                  marginTop: 2,
                  marginRight: 2,
                }}
              >
                <Alert severity="warning">
                  Are you sure you want to remove the account
                  <b> {tenantUser && tenantUser.userFullName}</b>? The account will no longer be able to login to Questri,
                  but the user and data will remain.
                </Alert>
              </Box>
            )}
            {confirmDelete && (
              <Box
                sx={{
                  width: '100%',
                  marginTop: 2,
                  marginRight: 2,
                }}
              >
                <Alert severity="warning">
                  Are you sure you want to remove the user
                  <b> {tenantUser && tenantUser.userFullName}</b> from this organization?
                  Any billing transactions or invoices associated with this user will lose the association. This action cannot be undone.
                </Alert>
              </Box>
            )}
          </Box>
          <Box>
            <Button
              onClick={onClose}
              disabled={deleting}
              sx={{ mr: 1 }}
              id="cancel-edit-button"
            >
              Cancel
            </Button>
            <LoadingButton
              disabled={deleting || saving}
              loading={saving}
              onClick={handleSaveName}
              color="primary"
              variant="contained"
              autoFocus
              id="confirm-edit-button"
            >
              Save
            </LoadingButton>
          </Box>
        </Box>
      </DialogActions>

      <AboutUserRolesModal
        open={openAboutRolesModal}
        onClose={() => setOpenAboutRolesModal(false)}
      />
      {
        openAddEditModal && (
          <AddEditModal
            open={openAddEditModal}
            horseAccess={selectedHorseAccess}
            horseId={selectedHorseAccess && selectedHorseAccess.horse.id}
            user={tenantUser}
            onClose={() => {
              setLoadingHorseAccess(true);
              setOpenAddEditModal(false);
              QuestriUserService.getUserHorseAccess(tenantUser.id).then((data) => {
                setLoadingHorseAccess(false);
                setHorseAccess(data);
              }).catch((error) => {
                console.error(error);
                setLoadingHorseAccess(false);
              })
            }}
          />
        )
      }
    </Dialog>
  );
};

UserProfileComponent.propTypes = {
  tenant: PropTypes.string,
  tenantUser: PropTypes.object,
  onClose: PropTypes.func,
  open: PropTypes.bool,
};
UserProfileComponent.defaultProps = {
  tenant: '',
  tenantUser: null,
  open: false,
  onClose: () => { },
};

export default UserProfileComponent;
